import { z } from 'zod';

// Default base URL PATH. `/api/v1` keeps requests on the same origin as the
// web app, so they go through the Vite dev proxy (and the prod reverse proxy)
// to the API server at `/api/v1/...`. The path-only default also matches what
// is documented in `.env.example`.
//
// The resolver below joins this with `window.location.origin` when running in
// a browser-like environment (dev, prod, jsdom test). MSW interceptors and
// `new URL()` calls inside fetch wrappers require an absolute URL, so leaving
// the value relative would crash the test suite. In any non-browser context
// (e.g. SSR, currently unused) we keep the value relative.
//
// An empty `VITE_API_BASE_URL` is coerced to this default so accidental
// misconfiguration in CI / preview deploys does not silently send requests
// to the WEB origin alone (which would bypass `/api/v1/` and miss the API).
const DEFAULT_PATH = '/api/v1';

const EnvSchema = z.object({
  VITE_API_BASE_URL: z
    .string()
    .default(DEFAULT_PATH)
    .transform((value) => (value.length > 0 ? value : DEFAULT_PATH)),
});

const parsed = EnvSchema.parse(import.meta.env);

function resolveBaseUrl(value: string): string {
  // Absolute URLs (http/https) and protocol-relative URLs are used as-is.
  if (/^https?:\/\//.test(value)) return value;
  // Path-only values are joined to the current origin so fetch + MSW always
  // see an absolute URL.
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${value.startsWith('/') ? value : `/${value}`}`;
  }
  return value;
}

/**
 * Better Auth's REST surface lives on the same origin under `/api/auth`.
 * Derived from `VITE_API_BASE_URL` so a single env var configures both
 * (typed v1 client + Better Auth client).
 */
function deriveAuthBaseUrl(apiBase: string): string {
  if (apiBase.endsWith('/api/v1')) return `${apiBase.slice(0, -'/api/v1'.length)}/api/auth`;
  if (apiBase.endsWith('/v1')) return `${apiBase.slice(0, -'/v1'.length)}/auth`;
  return `${apiBase.replace(/\/$/, '')}/auth`;
}

const apiBase = resolveBaseUrl(parsed.VITE_API_BASE_URL);

export const env = {
  VITE_API_BASE_URL: apiBase,
  VITE_AUTH_BASE_URL: deriveAuthBaseUrl(apiBase),
} as const;

export type Env = typeof env;
